import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { DeleteConfirmDialog } from '@/components/ui/DeleteConfirmDialog';
import Notification from '@/components/ui/Notification';
import usePlayerData from '@/hooks/usePlayerData';
import { deletePlayer, getErrorMessage, Player } from '@/lib/api';

export function Players() {
  const { t } = useTranslation(['players', 'common']);
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPlayers, setFilteredPlayers] = useState<Player[]>([]);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [playerToDelete, setPlayerToDelete] = useState<number | null>(null);

  // Notification state
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationSeverity, setNotificationSeverity] = useState<'success' | 'error'>('success');

  // Use custom hook to manage player data and state
  const {
    players,
    loading,
    error,
    total,
    handlePageChange: handleApiPageChange,
    handleRowsPerPageChange: handleApiRowsPerPageChange,
    refreshData,
  } = usePlayerData(rowsPerPage, page * rowsPerPage);

  /**
   * Open delete dialog
   */
  const handleOpenDeleteDialog = useCallback((id: number) => {
    setPlayerToDelete(id);
    setDeleteDialogOpen(true);
  }, []);

  /**
   * Close delete dialog
   */
  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setPlayerToDelete(null);
  }, []);

  /**
   * Player deletion process
   */
  const handleDeletePlayer = useCallback(async () => {
    if (playerToDelete === null) return;

    try {
      // Delete player using API
      const result = await deletePlayer(playerToDelete);

      // Display success message
      setNotificationMessage(result.meta.message || t('players:messages.deleteSuccess'));
      setNotificationSeverity('success');
      setNotificationOpen(true);
      refreshData(); // Refresh list
    } catch (err) {
      console.error('Failed to delete player:', err);
      // Convert error message to user-friendly format
      setNotificationMessage(getErrorMessage(err));
      setNotificationSeverity('error');
      setNotificationOpen(true);
    } finally {
      handleCloseDeleteDialog();
    }
  }, [playerToDelete, refreshData, handleCloseDeleteDialog, t]);

  /**
   * Close notification
   */
  const handleCloseNotification = useCallback(() => {
    setNotificationOpen(false);
  }, []);

  // Search filter
  useEffect(() => {
    if (!players) return;

    if (searchTerm) {
      const filtered = players.filter((player) =>
        player.id.toString().includes(searchTerm.toLowerCase()) ||
        player.playerCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (player.name && player.name.toLowerCase().includes(searchTerm.toLowerCase())),
      );
      setFilteredPlayers(filtered);
      setPage(0); // Reset page when searching
    } else {
      setFilteredPlayers(players);
    }
  }, [searchTerm, players]);

  // Pagination handling
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    handleApiPageChange(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    handleApiRowsPerPageChange(newRowsPerPage);
  };

  // Navigate to edit page
  const handleEdit = (id: number) => {
    navigate(`/players/edit?id=${id}`);
  };

  // Navigate to create page
  const handleCreate = () => {
    navigate('/players/new');
  };

  // Date formatting
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {t('players:title')}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          {t('players:buttons.newPlayer')}
        </Button>
      </Box>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Paper sx={{ width: '100%', mb: 2 }}>
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder={t('players:messages.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <TableContainer>
          <Table
            sx={{
              minWidth: 650,
              '& .MuiTableCell-root': { py: 0.5 }, // Apply to all TableCells
            }}
            aria-label={t('players:table.ariaLabel')}
            size="small"
          >
            <TableHead>
              <TableRow>
                <TableCell>{t('players:table.id')}</TableCell>
                <TableCell>{t('players:table.playerCode')}</TableCell>
                <TableCell>{t('players:table.name')}</TableCell>
                <TableCell>{t('players:table.apiKey')}</TableCell>
                <TableCell>{t('players:table.createdAt')}</TableCell>
                <TableCell align="right">{t('players:table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress size={24} />
                    <Typography variant="body2" sx={{ ml: 1, display: 'inline' }}>
                      {t('common:messages.loading')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredPlayers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    {t('players:messages.noPlayers')}
                  </TableCell>
                </TableRow>
              ) : (
                filteredPlayers.map((player) => (
                  <TableRow
                    key={player.id}
                    sx={{
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                    }}
                  >
                    <TableCell>{player.id}</TableCell>
                    <TableCell>{player.playerCode}</TableCell>
                    <TableCell>{player.name || '-'}</TableCell>
                    <TableCell>{player.apiKey}</TableCell>
                    <TableCell>{player.createdAt ? formatDate(player.createdAt) : '-'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('common:buttons.edit')}>
                        <IconButton
                          aria-label="edit"
                          color="primary"
                          onClick={() => handleEdit(player.id)}
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common:buttons.delete')}>
                        <IconButton
                          aria-label="delete"
                          color="error"
                          onClick={() => handleOpenDeleteDialog(player.id)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t('common:pagination.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => t('common:pagination.displayedRows', { from, to, count })}
        />
      </Paper>

      {/* Delete confirmation dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDeletePlayer}
        title={t('players:messages.deleteTitle')}
        message={t('players:messages.deleteConfirm')}
      />

      {/* Notification */}
      <Notification
        open={notificationOpen}
        message={notificationMessage}
        severity={notificationSeverity}
        onClose={handleCloseNotification}
      />
    </>
  );
}

export default Players;
